/**
 * ⚠️ **EVERY CATALOGUE AND CAPABILITY KEY HERE WAS VERIFIED AGAINST THE BUILT PACKAGE**, not
 * recalled — the first draft guessed six field paths and `QUERY_CATALOGUE` turned out to be an ARRAY
 * rather than an object, so the lookups all reported MISSING. A corpus of plausible-looking keys
 * would fail every COMPOSE entry and read as the model being wrong.
 */
export const COPILOT_CORPUS = [
    /* ── the matcher must resolve these: free, deterministic, checked in CI ───────────────────── */
    {
        question: 'which vehicles used the most fuel?',
        expect: { kind: 'MATCH', capability: 'fuel.used.rank' },
        why: 'The declared question of the consumption ranking, near enough verbatim.',
    },
    {
        question: 'which drivers keep speeding',
        expect: { kind: 'MATCH', capability: 'driver.repeat.behaviour' },
        why: 'A declared EXAMPLE of that capability, so the capability key is free to reinforce it — the ' +
            'rule that lets a key strengthen a match it did not create.',
    },
    {
        question: 'who are my worst drivers',
        expect: { kind: 'AMBIGUOUS', capabilities: ['driver.improvement', 'driver.repeat.behaviour'] },
        why: '⚠️ MEASURED at 0.462 against 0.450 — a 0.012 gap, so which one a reader is shown is a coin ' +
            'toss. Both readings are defensible: "worse" is a literal token of "improving or getting ' +
            'worse", and "worst drivers" means repeat offenders in ordinary use. Recorded as ambiguous ' +
            'rather than pinned, because a corpus entry demanding one of them would fail whenever ' +
            'somebody edited a declared phrase and would tell nobody anything. It was written as a ' +
            'MATCH first and the corpus test caught it.',
    },
    {
        question: 'which lorries went quiet',
        expect: { kind: 'MATCH', capability: 'fleet.silent' },
        why: 'A lorry is a vehicle (the synonym table), and going quiet is the silence capability.',
    },
    {
        question: 'show me all vehicles that have not reported for more than 2 hours',
        expect: { kind: 'MATCH', capability: 'fleet.silent' },
        why: 'One of the four questions a customer asked and got no answer to before `fleet.silent`.',
    },
    {
        question: 'did any vehicle lose fuel while parked',
        expect: { kind: 'MATCH', capability: 'fuel.loss.locate' },
        why: 'LOSE must reach LOSS — they differ at the fourth character, so the prefix match cannot ' +
            'bridge them and a closed inflection set does. `parked` must NOT decide the topic: as a ' +
            'synonym for idle it made this resolve to the idling report, which is the near miss the ' +
            'fuel-loss capability exists to answer.',
    },
    {
        question: 'where do we waste fuel idling?',
        expect: { kind: 'MATCH', capability: 'depot.idle.rank' },
        why: "The idling capability's own declared example. Guards the pair against over-correction.",
    },
    /* ── required abstentions ─────────────────────────────────────────────────────────────────── */
    {
        question: 'what is my cheapest depot',
        expect: { kind: 'ABSTAIN' },
        why: 'There is NO cost capability on the platform, so the only honest answer is that nothing can ' +
            'answer it. This resolved to `depot.idle.rank` at 0.45 on the capability KEY alone — an ' +
            'identifier somebody chose for a handler, not anything a reader wrote — which is why a key ' +
            'may now reinforce a match and never create one.',
    },
    {
        question: 'vehicles today',
        expect: { kind: 'ABSTAIN' },
        why: '⚠️ ADDED BECAUSE A MUTATION ESCAPED. Removing the WEAK-token floor — "a weak token may ' +
            'reinforce a match, never create one" — passed every other entry, because none of them is ' +
            'made ONLY of ubiquitous words. `vehicle`, `fleet` and `today` are declared by several ' +
            'capabilities, so without the floor a question carrying no topic at all resolves to whichever ' +
            'declares them most: "silent vehicles" once scored 0.46 against the IDLING report on the word ' +
            '"vehicles" and nothing else. This is the entry that fails when that floor goes.',
    },
    {
        question: 'give me hourly fuel level per vehicle for the last 7 days with location and ignition',
        expect: { kind: 'ABSTAIN' },
        why: 'Deferred, and it needs three things of which only one is data: tank capacity exists on no ' +
            'model, the hourly rollup carries no position by design, and OIP derives a fuel drop from ' +
            'nothing (2 FUEL_DROP events against 7,276 hourly fuel-level rows). An earlier example on ' +
            '`fuel.used.rank` pulled this above threshold and was DELETED for it.',
    },
    /* ── composition: needs a model ───────────────────────────────────────────────────────────── */
    {
        question: 'how many incidents of each type were raised in the last day',
        expect: {
            kind: 'COMPOSE',
            requires: {
                grain: ['incident'],
                dimensions: [['incident.type']],
                measures: [{ field: 'incident.count' }],
                windowHours: { min: 20, max: 30 },
            },
        },
        why: 'The first question that worked end to end. It also caught the prompt reading `windowHours` ' +
            'as optional when a filter on `incident.openedAt` was available — the line now says the ' +
            'window SETS the period and a filter narrows in addition, never instead.',
    },
    {
        question: 'how much fuel did each vehicle use last week',
        expect: {
            kind: 'COMPOSE',
            requires: {
                grain: ['vehicle-hour'],
                // Either names the vehicle; `vehicle-hour` reaches only the `vehicle` entity.
                dimensions: [['vehicle.name', 'vehicle.registration']],
                measures: [{ field: 'reading.totalFuelUsedL', aggregations: ['delta'] }],
                windowHours: { min: 150, max: 200 },
            },
        },
        why: '⚠️ THE ENTRY THIS WHOLE CORPUS EXISTS FOR. Observed composing ' +
            '`{ grain: vehicle, dimensions: [], measures: [count of vehicle.count] }` and answering ' +
            '"25 vehicles" — it validated, executed and was confidently wrong, and every gate passed. ' +
            '`delta` is required because the column is a LIFETIME accumulator: its sum is arithmetic ' +
            "over a running total and its max is the counter's face value.",
    },
    {
        question: 'how many trips did we make last week by how the distance was measured',
        expect: {
            kind: 'COMPOSE',
            requires: {
                grain: ['trip'],
                dimensions: [['trip.distanceSource']],
                measures: [{ field: 'trip.count' }],
                windowHours: { min: 150, max: 200 },
            },
        },
        why: 'Verified faithful end to end against a real model — 16 trips grouped by distance source.',
    },
    {
        question: 'how many vehicles are in each status',
        expect: {
            kind: 'COMPOSE',
            requires: {
                grain: ['vehicle'],
                dimensions: [['vehicle.status']],
                measures: [{ field: 'vehicle.count' }],
                // Deliberately none: `vehicle` has no time dimension, so a window is REFUSED here.
            },
        },
        why: 'The untimed grain, and it is the mirror of the windowHours failure — the model has sent ' +
            '`windowHours: null` on a grain that has none. "Omit the key" is not the same instruction as ' +
            '"send null", and a model reaches for null unless told.',
    },
    {
        question: 'which weekday do incidents happen on',
        expect: {
            kind: 'COMPOSE',
            requires: {
                grain: ['incident'],
                dimensions: [['incident.openedWeekday']],
                measures: [{ field: 'incident.count' }],
                windowHours: 'ANY',
            },
        },
        why: 'A BUCKETED dimension, which offers no operators and must not be filtered on — and which ' +
            "once produced a 500 on the evidence strip, because a bucket's declared type is what the " +
            'bucket PRODUCES while its path targets the DateTime it reduces. ⚠️ `windowHours: ANY` ' +
            'because the question names no period and the `incident` grain REFUSES a document without ' +
            'one — measured. This entry omitted it at first and marked the model wrong 5/5 for supplying ' +
            '168h, which it was right to do.',
    },
    {
        question: 'break down open incidents by risk level',
        expect: {
            kind: 'COMPOSE',
            requires: {
                grain: ['incident'],
                dimensions: [['incident.riskLevel']],
                measures: [{ field: 'incident.count' }],
                windowHours: 'ANY',
            },
        },
        why: '⚠️ **THE ONLY ENTRY THAT NAMES NO PERIOD AT ALL, and it exists because the other five all ' +
            'do — so the corpus could not measure the thing the reader-window change fixes.** The ' +
            "`incident` grain REFUSES a document with no window, so before the reader's own window " +
            'reached routing this question could only be answered by the model GUESSING a period nobody ' +
            'stated, and refused (`WINDOW_REQUIRED`) whenever it declined to guess. That is the common ' +
            'production shape: a reader sets the rail and types a question about a category. ' +
            "`windowHours: ANY` because either the model's guess or the period the reader had already " +
            'chosen is a correct answer — what must not happen is a refusal.',
    },
    {
        question: 'how far did each vehicle travel last week',
        expect: {
            kind: 'COMPOSE',
            requires: {
                /*
                 * ⚠️ EITHER route, because both are right and the model found the better one. A trip's
                 * distance is the MEASURED figure — `distanceSource` exists precisely to say whether it came
                 * from an odometer delta or from summed GPS hops — so `Trip` is the purpose-built grain and
                 * `reading.odometerKm[delta]` is the reading-level way to the same number.
                 */
                grain: ['trip', 'vehicle-hour'],
                dimensions: [['vehicle.name', 'vehicle.registration']],
                measures: [{ field: 'trip.distanceMetres' }, { field: 'reading.odometerKm' }],
                windowHours: { min: 150, max: 200 },
            },
        },
        why: '⚠️ **WRITTEN TO MEASURE A COLLATERAL COST, and it CORRECTED ITS OWN AUTHOR instead — the third ' +
            'expectation in this corpus to be wrong where the model was right.** It was added because every ' +
            'attempt to fix the fuel question by wording had been judged on that question alone, so a ' +
            'change that made the model safer there by making it stop recognising a cumulative counter AT ' +
            'ALL would have scored as an improvement. It demanded `reading.odometerKm[delta]`; the model ' +
            'composed `{grain: trip, measures: [trip.distanceMetres sum]}`, which is the better answer and ' +
            'the one the design prefers.\n\n' +
            '⚠️ So it does NOT measure what it was written for: the model never reaches for a counter here, ' +
            'because a better route exists. A control for that needs a question only a counter can answer — ' +
            'engine hours, where a trip duration is a different quantity (idling). Recorded rather than ' +
            'presented as coverage.\n\n' +
            'What it DOES pin is the grouping: "each vehicle" was answered with a fleet total, ' +
            '`dimensions: []`, and no completion fires because a trip belongs to one vehicle already so ' +
            '`acrossVehicles` is true. That is a real miss and the judge is right to call it one.',
    },
];
/**
 * Does this document answer the question the corpus entry asked?
 *
 * ⚠️ **IT ASSUMES THE DOCUMENT IS ALREADY ASKABLE.** `validateQuery` decides that and the harness
 * reports it as `QUERY_NOT_ASKABLE`, which is a DIFFERENT finding: the model read the catalogue
 * correctly and composed something disallowed, usually a gap in what the prompt explained. Folding
 * the two together would hide which of the prompt or the schema needs work.
 *
 * Pure, so every judgement below is settled by a unit case rather than by a model run.
 */
export function judgeFaithfulness(requires, doc) {
    const reasons = [];
    if (!requires.grain.includes(doc.grain)) {
        reasons.push(`grain is "${doc.grain}", and the question is about ${requires.grain.join(' or ')}`);
    }
    for (const want of requires.measures) {
        const matching = doc.measures.filter((m) => m.field === want.field);
        if (matching.length === 0) {
            /*
             * ⚠️ The fatal one, and the reason the corpus exists: a fuel question answered with a count of
             * vehicles is a document every gate accepts.
             */
            reasons.push(`no measure reads ${want.field}`);
            continue;
        }
        if (want.aggregations && !matching.some((m) => want.aggregations.includes(m.aggregation))) {
            reasons.push(`${want.field} is aggregated as ${matching.map((m) => m.aggregation).join('/')}, ` +
                `and answering the question needs ${want.aggregations.join(' or ')}`);
        }
    }
    for (const group of requires.dimensions ?? []) {
        if (!group.some((d) => doc.dimensions.includes(d))) {
            reasons.push(`nothing groups by ${group.join(' or ')}`);
        }
    }
    const w = requires.windowHours;
    if (w === 'ANY') {
        /*
         * The grain demands one and the question leaves it open, so any positive window is faithful — but
         * its ABSENCE is not, because such a document is refused outright.
         */
        if (doc.windowHours === undefined) {
            reasons.push('this grain requires a window and the document has none');
        }
    }
    else if (w) {
        if (doc.windowHours === undefined) {
            reasons.push(`the question names a period and the document has no window`);
        }
        else if (doc.windowHours < w.min || doc.windowHours > w.max) {
            reasons.push(`windowHours is ${doc.windowHours}, outside the ${w.min}–${w.max} the question implies`);
        }
    }
    else if (doc.windowHours !== undefined) {
        /*
         * ⚠️ Reported rather than ignored. On an untimed grain a window is REFUSED outright, so this is
         * usually already a `QUERY_NOT_ASKABLE` — but where it is merely unasked-for it is still the
         * model inventing a period the reader did not mention, which narrows the answer silently.
         */
        reasons.push(`the question names no period and the document invents a ${doc.windowHours}h window`);
    }
    return reasons.length === 0 ? { faithful: true } : { faithful: false, reasons };
}
/** Everything a model has to answer. Split out so the free half can run without it. */
export const CORPUS_COMPOSE_ENTRIES = COPILOT_CORPUS.filter((e) => e.expect.kind === 'COMPOSE');
/** Everything decidable for free. */
export const CORPUS_MATCHER_ENTRIES = COPILOT_CORPUS.filter((e) => e.expect.kind !== 'COMPOSE');
//# sourceMappingURL=copilot-corpus.js.map